import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorService } from '@okcargo-apps/shared-application';
import { CheckVersionService } from '@okcargo-apps/utils-ionic';
import { Observable, catchError, from, switchMap, tap, throwError } from 'rxjs';
import { JwtService } from '../auth/jwt.service';
import { APP_CONFIG } from '../environment/environment.token';
import { INCLUDE_AUTH_TOKEN } from './api.tokens';

const OVERRIDE_PRODUCTION_USERS = [
  {
    email: 'conductor@okcargo.io',
    phone: '666666670',
  },
];

const shouldOverrideLoginRequest = (req: HttpRequest<unknown>): boolean => {
  if (!req.url.includes('/auth/login')) return false;

  try {
    const body = req.body as { username?: string };
    if (!body?.username) return false;

    return OVERRIDE_PRODUCTION_USERS.some((user) => user.email === body.username || user.phone === body.username);
  } catch {
    return false;
  }
};

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(APP_CONFIG);
  const jwtService = inject(JwtService);
  const router = inject(Router);
  const errorService = inject(ErrorService);
  const checkVersionService = inject(CheckVersionService);

  let revisedReq = req.clone();

  if (
    shouldOverrideLoginRequest(req) ||
    OVERRIDE_PRODUCTION_USERS.some((user) => user.email === jwtService.username || user.phone === jwtService.username)
  ) {
    revisedReq = req.clone({
      url: req.url.replace(config.apiUrl, config.apiDemoUrl),
    });
  }

  if (req.context.get(INCLUDE_AUTH_TOKEN)) {
    const authReq = addAuthToken(revisedReq, jwtService);
    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // TODO IMPORTANT we need a 401 error code
        if (error.status === 0 || error.status === 401) {
          return handle401Error(revisedReq, next, jwtService, router);
        } else {
          handleError(error, router, errorService, checkVersionService);
          return throwError(() => error);
        }
      }),
    );
  }

  return next(revisedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      handleError(error, router, errorService, checkVersionService);
      return throwError(() => error);
    }),
  );
};

function addAuthToken(req: HttpRequest<unknown>, jwtService: JwtService): HttpRequest<unknown> {
  const accessToken = jwtService.accessToken;
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  jwtService: JwtService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  return jwtService.requestNewAuthToken$().pipe(
    switchMap((newToken: string) => {
      jwtService.setAccessToken(newToken);
      const authReq = addAuthToken(req, jwtService);
      return next(authReq);
    }),
    catchError((error) => {
      jwtService.clearTokens();
      // TODO review this
      router.navigate(['/login']);
      return throwError(() => new Error(error.message));
    }),
  );
}

// TODO implement this
function handleError(
  error: HttpErrorResponse,
  router: Router,
  errorService: ErrorService,
  checkVersionService: CheckVersionService,
): Observable<never> {
  if (error.status === 400 || error.status === 404 || error.status === 405) {
    return throwError(() => error);
  }

  const errorInfo = {
    message: error?.message,
    status: error?.status,
    detail: error?.error?.detail,
  };
  errorService.setError(errorInfo);

  return from(checkVersionService.forceCheck()).pipe(
    tap(() => {
      const needsUpdate = checkVersionService.isUpdateRequired();

      if (needsUpdate === true) {
        router.navigate(['/update-required'], { replaceUrl: true });
      } else {
        router.navigate(['/error'], { replaceUrl: true });
      }
    }),
    switchMap(() => throwError(() => error)),
  );
}
